import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, MapPin, Zap, Droplets, SlidersHorizontal,
  X, Check, ArrowUpDown, ChevronDown, Users, Heart, Sparkles, ChevronRight
} from 'lucide-react';
import { db } from '../lib/firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { computeStandSuggestions } from '../utils/matching';

// ─── Types & constants ────────────────────────────────────────────────────────
interface Stand {
  id: string;
  name: string;
  standType?: string;
  description?: string;
  logo?: string;
  bannerUrl?: string;
  galleryUrls?: string[];
  region?: string;
  priceRange?: string;
  revenueRange?: string;
  experienceRange?: string;
  musicStyles?: string[];
  needsElectricity?: boolean;
  waterNeeds?: boolean;
  width?: number;
  depth?: number;
  staffBadges?: number;
  videoUrl?: string;
  insuranceUrl?: string;
  isDraft?: boolean;
}

const CATEGORY_TABS = [
  'Tous', 'Food Truck', 'Snacking', 'Artisanat', 'Mode',
  'Vinyles', 'Tatouage', 'Engagé', 'Services', 'Partenaires',
];

const STAND_TYPE_MAP: Record<string, string> = {
  food:        'Food Truck',
  snacking:    'Snacking',
  craft:       'Artisanat',
  fashion:     'Mode',
  vinyls:      'Vinyles',
  tattoo:      'Tatouage',
  associative: 'Engagé',
  partners:    'Partenaires',
  service:     'Services',
  other:       'Autre',
};

const PRICE_LABELS: Record<string, string> = {
  budget:  '€',
  medium:  '€€',
  premium: '€€€',
  luxury:  '€€€€',
};

const EXP_LABELS: Record<string, string> = {
  none:  'Premier festival',
  '1-3': 'Émergeant',
  '4-10':'Confirmé',
  '11+': 'Expert',
};

const REGIONS = [
  'Île-de-France', 'Auvergne-Rhône-Alpes', 'Provence-Alpes-Côte d\'Azur',
  'Occitanie', 'Nouvelle-Aquitaine', 'Bretagne', 'Pays de la Loire',
  'Normandie', 'Grand Est', 'Hauts-de-France', 'Centre-Val de Loire',
  'Bourgogne-Franche-Comté', 'Corse',
];

const SPECIAL_CHIPS = ['Avec vidéo', 'Vérifié'];

// Festival de l'organisateur — champs nécessaires au moteur de matching
// (mêmes critères que côté exposant : région, style, prix, taille, dates).
interface OrganizerFestival {
  id: string;
  name?: string;
  endDate?: string;
  location?: string;
  region?: string;
  musicStyles?: string[];
  standDetails?: Record<string, unknown>;
  totalStands?: number;
  capacity?: number;
  expectedPriceRange?: string;
}

// ─── Stand card ───────────────────────────────────────────────────────────────
function StandCard({ stand, onClick, matchScore }: { stand: Stand; onClick: () => void; matchScore?: number }) {
  const cover = stand.bannerUrl || stand.galleryUrls?.[0] || stand.logo || null;
  const typeLabel = stand.standType ? STAND_TYPE_MAP[stand.standType] : null;

  return (
    <div onClick={onClick} className="group cursor-pointer flex flex-col">
      {/* Image */}
      <div className="relative aspect-[4/3] overflow-hidden rounded-2xl mb-3 bg-[#121212]">
        {typeof matchScore === 'number' && (
          <div className="absolute top-3 right-3 z-10 flex items-center gap-1 bg-[#FF5C00] text-white text-xs font-black px-2.5 py-1 rounded-full shadow-lg">
            <Sparkles className="w-3 h-3" /> {matchScore}%
          </div>
        )}
        {cover ? (
          <img src={cover} alt={stand.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#FF5C00]/10 to-[#1a1a1a] flex items-center justify-center">
            <span className="text-4xl font-black text-[#FF5C00]/30">{stand.name?.charAt(0)?.toUpperCase()}</span>
          </div>
        )}

        {/* Category badge */}
        {typeLabel && (
          <div className="absolute top-3 left-3 bg-[#121212]/90 border border-white/10 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-bold text-white shadow-sm">
            {typeLabel}
          </div>
        )}

        {/* Tech icons */}
        <div className="absolute bottom-3 right-3 flex gap-1.5">
          {stand.needsElectricity && (
            <div className="bg-yellow-500/20 border border-yellow-500/30 backdrop-blur-sm rounded-full p-1">
              <Zap className="w-3 h-3 text-yellow-400" />
            </div>
          )}
          {stand.waterNeeds && (
            <div className="bg-blue-500/20 border border-blue-500/30 backdrop-blur-sm rounded-full p-1">
              <Droplets className="w-3 h-3 text-blue-400" />
            </div>
          )}
        </div>
      </div>

      {/* Info */}
      <div className="flex justify-between items-start mb-1">
        <h3 className="font-bold text-base truncate pr-2 text-white">{stand.name || 'Stand sans nom'}</h3>
        {stand.priceRange && (
          <span className="font-semibold text-xs text-white bg-white/10 px-2 py-[2px] rounded-md shrink-0">
            {PRICE_LABELS[stand.priceRange]}
          </span>
        )}
      </div>

      {stand.region && (
        <p className="text-sm text-gray-400 flex items-center gap-1">
          <MapPin className="w-3.5 h-3.5 shrink-0" />{stand.region}
        </p>
      )}

      {stand.experienceRange && (
        <p className="text-sm text-[#FF5C00] font-semibold mt-0.5">
          {EXP_LABELS[stand.experienceRange]}
        </p>
      )}

      {(stand.width || stand.staffBadges) && (
        <div className="mt-1 text-sm flex items-center gap-3 text-gray-500">
          {stand.width && stand.depth && <span>{stand.width}m × {stand.depth}m</span>}
          {!!stand.staffBadges && <span className="flex items-center gap-0.5"><Users className="w-3.5 h-3.5" />{stand.staffBadges}</span>}
        </div>
      )}

      <button
        onClick={e => { e.stopPropagation(); onClick(); }}
        className="mt-4 w-full h-12 bg-white/5 hover:bg-white/10 text-white text-[11px] md:text-sm font-black uppercase tracking-widest rounded-xl transition-colors text-center border border-white/10 active:scale-[0.98]"
      >
        Voir le stand
      </button>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function ExploreStandsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [stands, setStands] = useState<Stand[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Matching — festivals de l'organisateur + mode d'affichage
  const [myFestivals, setMyFestivals] = useState<OrganizerFestival[]>([]);
  const [selectedFestivalId, setSelectedFestivalId] = useState<string>('');
  const [viewMode, setViewMode] = useState<'recommended' | 'all'>('recommended');

  // Filters
  const [activeCategory, setActiveCategory] = useState('Tous');
  const [regionFilter, setRegionFilter] = useState('');
  const [selectedChips, setSelectedChips] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<'name' | 'region' | 'experience'>('name');
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false);

  // Sidebar filters
  const [electricityFilter, setElectricityFilter] = useState<'all' | 'yes' | 'no'>('all');
  const [waterFilter, setWaterFilter] = useState<'all' | 'yes' | 'no'>('all');
  const [experienceFilter, setExperienceFilter] = useState('all');
  const [priceFilter, setPriceFilter] = useState('all');

  useEffect(() => {
    getDocs(collection(db, 'stands')).then(snap => {
      setStands(snap.docs.map(d => ({ id: d.id, ...d.data() }) as Stand));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  // Festivals de l'organisateur — pour choisir contre lequel matcher les stands.
  useEffect(() => {
    if (!user) return;
    getDocs(query(collection(db, 'festivals'), where('ownerId', '==', user.uid))).then(snap => {
      const fests = snap.docs.map(d => ({ id: d.id, ...d.data() }) as OrganizerFestival);
      setMyFestivals(fests);
      if (fests.length > 0) setSelectedFestivalId(prev => prev || fests[0].id);
      else setViewMode('all'); // pas de festival → le mode recommandé n'a pas de sens
    }).catch(() => {});
  }, [user]);

  const selectedFestival = myFestivals.find(f => f.id === selectedFestivalId) || null;

  // Suggestions « Stands pour vous » — Couche 1 (éligibilité) + Couche 2
  // (score de pertinence), mêmes pondérations que le côté exposant.
  const suggestions = useMemo(() => {
    if (!selectedFestival) return [];
    const publishedStands = stands.filter(s => !s.isDraft);
    return computeStandSuggestions(publishedStands, selectedFestival, { limit: 24 });
  }, [stands, selectedFestival]);

  const showRecommended = viewMode === 'recommended' && !!selectedFestival;

  const filtered = stands
    .filter(s => {
      if (s.isDraft) return false;
      const q = searchQuery.toLowerCase();
      if (q && !s.name?.toLowerCase().includes(q) && !s.description?.toLowerCase().includes(q)) return false;
      if (activeCategory !== 'Tous') {
        const key = Object.entries(STAND_TYPE_MAP).find(([, v]) => v === activeCategory)?.[0];
        if (s.standType !== key) return false;
      }
      if (regionFilter && s.region !== regionFilter) return false;
      if (selectedChips.includes('Avec vidéo') && !s.videoUrl) return false;
      if (selectedChips.includes('Vérifié') && !s.insuranceUrl) return false;
      if (electricityFilter === 'yes' && !s.needsElectricity) return false;
      if (electricityFilter === 'no' && s.needsElectricity) return false;
      if (waterFilter === 'yes' && !s.waterNeeds) return false;
      if (waterFilter === 'no' && s.waterNeeds) return false;
      if (experienceFilter !== 'all' && s.experienceRange !== experienceFilter) return false;
      if (priceFilter !== 'all' && s.priceRange !== priceFilter) return false;
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'region') return (a.region || '').localeCompare(b.region || '');
      if (sortBy === 'experience') {
        const order = ['11+', '4-10', '1-3', 'none', ''];
        return order.indexOf(a.experienceRange || '') - order.indexOf(b.experienceRange || '');
      }
      return (a.name || '').localeCompare(b.name || '');
    });

  const activeFiltersCount = [
    electricityFilter !== 'all',
    waterFilter !== 'all',
    experienceFilter !== 'all',
    priceFilter !== 'all',
  ].filter(Boolean).length;

  const hasActiveFilters = activeCategory !== 'Tous' || selectedChips.length > 0 ||
    searchQuery.length > 0 || !!regionFilter || activeFiltersCount > 0;

  const reset = () => {
    setActiveCategory('Tous');
    setSelectedChips([]);
    setSearchQuery('');
    setRegionFilter('');
    setElectricityFilter('all');
    setWaterFilter('all');
    setExperienceFilter('all');
    setPriceFilter('all');
  };


  return (
    <div className="min-h-screen bg-black text-white font-sans">

      {/* ── Matching : festival ciblé + mode d'affichage ── */}
      {myFestivals.length > 0 && (
        <div className="max-w-7xl mx-auto px-6 pt-8">
          <div className="flex flex-wrap items-center gap-3 mb-6">
            {myFestivals.length > 1 && (
              <div className="relative shrink-0">
                <select
                  value={selectedFestivalId}
                  onChange={e => setSelectedFestivalId(e.target.value)}
                  className="appearance-none bg-white/5 border border-white/10 rounded-2xl pl-4 pr-9 py-2.5 text-sm font-semibold text-white focus:outline-none focus:border-[#FF5C00] cursor-pointer"
                >
                  {myFestivals.map(f => <option key={f.id} value={f.id}>{f.name || 'Festival sans nom'}</option>)}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            )}

            <div className="flex gap-1 bg-white/5 p-1 rounded-2xl border border-white/5">
              <button
                onClick={() => setViewMode('recommended')}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all ${
                  viewMode === 'recommended' ? 'bg-[#FF5C00] text-white shadow-sm' : 'text-gray-400 hover:text-white'
                }`}
              >
                <Sparkles className="w-3.5 h-3.5" /> Recommandés pour vous
              </button>
              <button
                onClick={() => setViewMode('all')}
                className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${
                  viewMode === 'all' ? 'bg-white text-black shadow-sm' : 'text-gray-400 hover:text-white'
                }`}
              >
                Tous les stands
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Top bar (same structure as HomePage) — masquée en mode recommandé,
           les filtres manuels n'ont pas de sens sur une liste déjà triée par
           pertinence pour le festival sélectionné. ── */}
      {!showRecommended && (
      <div className={`max-w-7xl mx-auto px-6 pb-4 ${myFestivals.length > 0 ? '' : 'pt-8'}`}>

        {/* Search + sort */}
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Nom du stand, type d'activité, mot-clé..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-2xl py-3 pl-12 pr-4 text-white focus:outline-none focus:border-[#FF5C00] focus:ring-1 focus:ring-[#FF5C00] transition-all placeholder:text-gray-500"
            />
          </div>

          <div className="relative hidden md:block shrink-0">
            <ArrowUpDown className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value as typeof sortBy)}
              className="appearance-none bg-white/5 border border-white/10 rounded-2xl pl-9 pr-8 py-3 text-sm text-gray-300 focus:outline-none focus:border-[#FF5C00] cursor-pointer"
            >
              <option value="name">Par nom</option>
              <option value="region">Par région</option>
              <option value="experience">Par expérience</option>
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {/* Région + chips rapides */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2 no-scrollbar mb-4">
          {/* Région select */}
          <div className="relative shrink-0">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
            <select
              value={regionFilter}
              onChange={e => setRegionFilter(e.target.value)}
              className={`appearance-none pl-8 pr-7 py-2 rounded-full text-sm font-semibold transition-all border cursor-pointer focus:outline-none ${
                regionFilter
                  ? 'bg-[#FF5C00] text-white border-[#FF5C00] shadow-[0_0_15px_rgba(255,92,0,0.35)]'
                  : 'bg-white/5 text-gray-400 border-white/10 hover:bg-white/10 hover:text-white'
              }`}
            >
              <option value="">Toute la France</option>
              {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
          </div>

          {/* Special chips */}
          {SPECIAL_CHIPS.map(chip => {
            const active = selectedChips.includes(chip);
            return (
              <button
                key={chip}
                onClick={() => setSelectedChips(prev => active ? prev.filter(c => c !== chip) : [...prev, chip])}
                className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-semibold transition-all border flex items-center gap-2 shrink-0 ${
                  active
                    ? 'bg-[#FF5C00] text-white border-[#FF5C00] shadow-[0_0_15px_rgba(255,92,0,0.4)]'
                    : 'bg-white/5 text-gray-400 border-white/10 hover:bg-white/10 hover:text-white'
                }`}
              >
                {chip === 'Vérifié' && <Check className="w-3.5 h-3.5" />}
                {chip}
              </button>
            );
          })}
        </div>

        {/* Category tabs */}
        <div className="flex space-x-1 overflow-x-auto pb-2 no-scrollbar mb-3">
          {CATEGORY_TABS.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveCategory(tab)}
              className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-semibold transition-all border ${
                activeCategory === tab
                  ? 'bg-white text-black border-white'
                  : 'bg-transparent text-gray-400 border-transparent hover:text-white'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Bottom row: reset chip + Filtres button */}
        <div className="flex items-center justify-between">
          <div className="flex space-x-2 overflow-x-auto pb-1 no-scrollbar">
            {hasActiveFilters && (
              <button
                onClick={reset}
                className="whitespace-nowrap px-4 py-2 rounded-full text-sm font-semibold border border-white/10 bg-white/5 text-gray-400 hover:text-white flex items-center gap-2"
              >
                <X className="w-3.5 h-3.5" /> Réinitialiser
              </button>
            )}
          </div>

          <button
            onClick={() => setIsFilterSidebarOpen(true)}
            className="relative hidden md:flex items-center space-x-2 border border-white/10 bg-white/5 hover:bg-white/10 px-5 py-2.5 rounded-full text-sm font-bold text-white hover:border-[#FF5C00]/50 transition-colors ml-4 shrink-0"
          >
            <SlidersHorizontal className="w-4 h-4" />
            <span>Filtres</span>
            {activeFiltersCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-[#FF5C00] text-black w-5 h-5 flex items-center justify-center rounded-full text-[10px] font-black border-2 border-black">
                {activeFiltersCount}
              </span>
            )}
          </button>
        </div>

        {/* Results count */}
        {!loading && (
          <p className="mt-3 text-xs text-gray-500">
            {filtered.length} stand{filtered.length !== 1 ? 's' : ''} trouvé{filtered.length !== 1 ? 's' : ''}
          </p>
        )}
      </div>
      )}

      {/* ── Grid ── */}
      <main className="max-w-7xl mx-auto px-6 py-6 pb-24">
        {showRecommended ? (
          loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="w-8 h-8 border-4 border-[#FF5C00]/20 border-t-[#FF5C00] rounded-full animate-spin" />
            </div>
          ) : suggestions.length === 0 ? (
            <div className="text-center py-20">
              <Sparkles className="w-10 h-10 text-gray-700 mx-auto mb-4" />
              <p className="text-gray-400 mb-2">Aucun stand suffisamment pertinent pour l'instant.</p>
              <p className="text-gray-600 text-sm mb-4">
                Complétez le profil de <strong>{selectedFestival?.name}</strong> (région, style musical, fourchette de prix) pour affiner les suggestions, ou explorez tous les stands.
              </p>
              <button onClick={() => setViewMode('all')} className="px-6 py-3 bg-white/10 hover:bg-white/15 text-white font-bold rounded-xl transition-colors">
                Voir tous les stands
              </button>
            </div>
          ) : (
            <>
              <p className="text-xs text-gray-500 mb-4 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-[#FF5C00]" />
                {suggestions.length} stand{suggestions.length !== 1 ? 's' : ''} pertinent{suggestions.length !== 1 ? 's' : ''} pour <strong className="text-white">{selectedFestival?.name}</strong>
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 xl:gap-8">
                {suggestions.map(({ stand, score }) => (
                  <StandCard
                    key={stand.id}
                    stand={stand}
                    matchScore={score}
                    onClick={() => navigate(`/stand-detail/${stand.id}`)}
                  />
                ))}
              </div>
            </>
          )
        ) : loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="w-8 h-8 border-4 border-[#FF5C00]/20 border-t-[#FF5C00] rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-400 mb-4">
              {hasActiveFilters ? 'Aucun stand trouvé avec ces filtres.' : 'Aucun stand disponible pour le moment.'}
            </p>
            {hasActiveFilters && (
              <button onClick={reset} className="px-6 py-3 bg-[#FF5C00] text-black font-bold rounded-xl">
                Réinitialiser les filtres
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 xl:gap-8">
            {filtered.map(stand => (
              <StandCard
                key={stand.id}
                stand={stand}
                onClick={() => navigate(`/stand-detail/${stand.id}`)}
              />
            ))}
          </div>
        )}
      </main>

      {/* ── Filter sidebar (same as HomePage) ── */}
      {isFilterSidebarOpen && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setIsFilterSidebarOpen(false)} />
          <div className="relative w-full max-w-sm sm:max-w-md bg-black/70 backdrop-blur-3xl border-l border-white/10 h-full flex flex-col pt-6 pb-8 px-6 sm:px-8 shadow-[-20px_0_40px_rgba(0,0,0,0.5)] animate-in slide-in-from-right">

            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-black">Filtres Avancés</h2>
              <button onClick={() => setIsFilterSidebarOpen(false)} className="p-2 hover:bg-white/10 rounded-full transition-colors bg-white/5">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto pr-2 space-y-10 no-scrollbar">

              {/* Expérience */}
              <div>
                <label className="block text-sm font-bold text-gray-300 mb-4">Niveau d'expérience</label>
                <div className="space-y-2">
                  {[
                    { id: 'all',   label: 'Toute expérience' },
                    { id: 'none',  label: 'Premier festival' },
                    { id: '1-3',   label: 'Émergeant (1–3 festivals)' },
                    { id: '4-10',  label: 'Confirmé (4–10 festivals)' },
                    { id: '11+',   label: 'Expert (10+ festivals)' },
                  ].map(opt => (
                    <label key={opt.id} className="flex items-center group cursor-pointer bg-white/5 hover:bg-white/10 border border-white/5 hover:border-white/20 p-4 rounded-xl transition-all">
                      <div className={`w-5 h-5 rounded flex items-center justify-center border transition-all mr-4 shrink-0 ${experienceFilter === opt.id ? 'bg-[#FF5C00] border-[#FF5C00]' : 'border-white/30 bg-black/50'}`}>
                        {experienceFilter === opt.id && <Check className="w-3.5 h-3.5 text-black" />}
                      </div>
                      <input type="radio" className="hidden" checked={experienceFilter === opt.id} onChange={() => setExperienceFilter(opt.id)} />
                      <span className={`${experienceFilter === opt.id ? 'text-white' : 'text-gray-400'} transition-colors font-medium`}>{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Panier moyen */}
              <div>
                <label className="block text-sm font-bold text-gray-300 mb-4">Panier moyen</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { id: 'all',    label: 'Tous' },
                    { id: 'budget', label: '€ · Budget' },
                    { id: 'medium', label: '€€ · Moyen' },
                    { id: 'premium',label: '€€€ · Premium' },
                    { id: 'luxury', label: '€€€€ · Luxe' },
                  ].map(opt => (
                    <button key={opt.id} onClick={() => setPriceFilter(opt.id)}
                      className={`px-3 py-3 rounded-xl text-sm font-semibold border transition-all text-left ${priceFilter === opt.id ? 'bg-[#FF5C00]/10 border-[#FF5C00] text-[#FF5C00]' : 'bg-white/5 border-white/10 text-gray-400 hover:text-white hover:border-white/30'}`}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Besoins techniques */}
              <div>
                <label className="block text-sm font-bold text-gray-300 mb-4">Besoins techniques</label>
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-widest font-bold mb-2">Électricité</p>
                    <div className="flex gap-2">
                      {(['all', 'yes', 'no'] as const).map(v => (
                        <button key={v} onClick={() => setElectricityFilter(v)}
                          className={`flex-1 py-2.5 rounded-xl text-sm font-bold border transition-all ${electricityFilter === v ? 'bg-[#FF5C00] border-[#FF5C00] text-black' : 'bg-white/5 border-white/10 text-gray-400 hover:text-white'}`}>
                          {v === 'all' ? 'Peu importe' : v === 'yes' ? 'Requise' : 'Non requise'}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-widest font-bold mb-2">Raccordement eau</p>
                    <div className="flex gap-2">
                      {(['all', 'yes', 'no'] as const).map(v => (
                        <button key={v} onClick={() => setWaterFilter(v)}
                          className={`flex-1 py-2.5 rounded-xl text-sm font-bold border transition-all ${waterFilter === v ? 'bg-[#FF5C00] border-[#FF5C00] text-black' : 'bg-white/5 border-white/10 text-gray-400 hover:text-white'}`}>
                          {v === 'all' ? 'Peu importe' : v === 'yes' ? 'Requis' : 'Non requis'}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-6 border-t border-white/10 mt-6 shrink-0">
              <button
                onClick={() => setIsFilterSidebarOpen(false)}
                className="w-full bg-[#FF5C00] hover:bg-[#eb5500] text-black font-bold py-3.5 rounded-xl transition-all shadow-[0_0_20px_rgba(255,92,0,0.3)]"
              >
                Afficher les résultats {activeFiltersCount > 0 ? `(${activeFiltersCount})` : ''}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
